/**
 * PortalMaps — read-only floor plans + historical heatmap overlay.
 *
 * Each device marker is interactive: clicking it opens a rich tooltip with
 * zone, type, subtype, location, last finding (with activity level, pest
 * type, observation) and number of events in the selected period.
 *
 * Colors match the admin/tech canvas:
 *   - Zone Verde   → green (cebaderos exterior)
 *   - Zone Amarilla → yellow (cebaderos perímetro)
 *   - Zone Roja     → red (trampas interior)
 *   - Lámparas UV   → violet (regardless of zone)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Map as MapIcon, Flame, BarChart3, Info, MapPin, Activity, Calendar as CalendarIcon, Tag, Layers, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import {
    TRAP_FINDINGS_TREX_MULTI, TRAP_FINDINGS_GATO_BANDEJA, BAIT_FINDINGS, LAMP_FINDINGS,
} from "@/lib/inspectionCatalog";

// Build a flat lookup: finding_code -> human label (merging all catalogs)
const FINDING_LBL = (() => {
    const out = {};
    [TRAP_FINDINGS_TREX_MULTI, TRAP_FINDINGS_GATO_BANDEJA, BAIT_FINDINGS, LAMP_FINDINGS].forEach((arr) => {
        (arr || []).forEach((f) => { if (f?.v) out[f.v] = f.l; });
    });
    // Older / synthetic codes used in earlier iterations
    Object.assign(out, {
        intact: "Cebo intacto",
        with_insects: "Con insectos",
        damaged: "Dañado",
        missing_device: "Faltante",
    });
    return out;
})();

/* ----------------------------- Color tokens ----------------------------- */
const ZONE_COLOR = {
    verde: "#16a34a",
    amarilla: "#eab308",
    roja: "#dc2626",
};
const UV_COLOR = "#9333ea"; // violet — matches admin/tech canvas

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.2;

const colorForDevice = (d) =>
    d?.device_type === "lampara_uv" ? UV_COLOR : ZONE_COLOR[d?.zone] || "#64748b";

/* ----------------------------- Label helpers ----------------------------- */
const TYPE_LBL = {
    cebadero_cebo: "Cebadero",
    trampa: "Trampa",
    lampara_uv: "Lámpara UV",
};
const ZONE_LBL = {
    verde: "Verde (exterior)",
    amarilla: "Amarilla (perímetro)",
    roja: "Roja (interior)",
};
const TRAP_SUBTYPE_LBL = {
    gato_papel: "Gato papel",
    bandeja: "Bandeja",
    multicaptura: "Multicaptura",
    trex: "T-Rex",
    caja: "Cebadero de caja",
    tubo: "Cebadero de tubo",
};
const ACTIVITY_LBL = {
    none: { label: "Nula", cls: "bg-slate-200 text-slate-700" },
    low: { label: "Baja", cls: "bg-emerald-100 text-emerald-800" },
    medium: { label: "Media", cls: "bg-amber-100 text-amber-800" },
    high: { label: "Alta", cls: "bg-red-100 text-red-800" },
};
const PEST_LBL = {
    raton: "Ratón",
    rata: "Rata",
    insectos_rastreros: "Insectos rastreros",
    insectos_voladores: "Insectos voladores",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("es-SV", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso.slice(0, 10); }
};

/* ----------------------------- Marker + Popover ----------------------------- */
function DeviceMarker({ device, summary, months }) {
    const color = colorForDevice(device);
    const subtype = device.trap_subtype && TRAP_SUBTYPE_LBL[device.trap_subtype];
    const last = summary?.last_inspection;
    const events = summary?.events_count ?? 0;
    const activity = ACTIVITY_LBL[last?.activity_level] || null;

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                    style={{ left: `${device.position_x}%`, top: `${device.position_y}%` }}
                    data-testid={`marker-${device.id}`}
                    aria-label={`Detalles de ${device.device_number}`}
                >
                    <div
                        className="relative px-1.5 py-0.5 rounded-full border-2 bg-white text-[10px] font-bold shadow-sm leading-tight cursor-pointer transition-transform group-hover:scale-110"
                        style={{ borderColor: color, color }}
                    >
                        {device.device_number}
                        {/* info pill — visual hint there's more info */}
                        <span
                            className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-white border flex items-center justify-center shadow-sm"
                            style={{ borderColor: color }}
                            data-testid={`marker-info-icon-${device.id}`}
                        >
                            <Info className="w-2.5 h-2.5" style={{ color }} />
                        </span>
                    </div>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" data-testid={`marker-popover-${device.id}`}>
                {/* Header strip */}
                <div className="px-4 py-3 rounded-t-md text-white" style={{ background: color }}>
                    <div className="flex items-center justify-between">
                        <div className="font-bold text-base leading-tight">{device.device_number}</div>
                        <Badge className="bg-white/25 hover:bg-white/25 text-white border-0 text-[10px]">
                            {ZONE_LBL[device.zone] || device.zone}
                        </Badge>
                    </div>
                    <div className="text-xs opacity-90 mt-0.5">
                        {TYPE_LBL[device.device_type] || device.device_type}
                        {subtype && ` · ${subtype}`}
                    </div>
                </div>

                {/* Body */}
                <div className="p-3 space-y-3 text-xs">
                    {device.area_name && (
                        <div className="flex items-start gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                            <div>
                                <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Ubicación</div>
                                <div className="font-medium">{device.area_name}</div>
                            </div>
                        </div>
                    )}

                    <div className="flex items-start gap-1.5">
                        <Activity className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Histórico</div>
                            <div className="font-medium">
                                {events === 0 ? "Sin eventos" : (
                                    <>{events} {events === 1 ? "evento" : "eventos"} en los últimos {months} meses</>
                                )}
                            </div>
                        </div>
                    </div>

                    {last ? (
                        <div className="border-t pt-2.5 space-y-2">
                            <div className="text-[10px] uppercase text-muted-foreground tracking-wide flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Último hallazgo
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="font-semibold">{FINDING_LBL[last.finding_code] || last.finding_code || "—"}</span>
                                {activity && (
                                    <Badge className={`${activity.cls} border-0 text-[10px]`}>
                                        Actividad {activity.label}
                                    </Badge>
                                )}
                            </div>
                            {last.pest_type && (
                                <div className="text-[11px]">
                                    <span className="text-muted-foreground">Tipo de plaga: </span>
                                    <b>{PEST_LBL[last.pest_type] || last.pest_type}</b>
                                </div>
                            )}
                            {device.device_type === "lampara_uv" && (last.insect_count ?? 0) > 0 && (
                                <div className="text-[11px]">
                                    <span className="text-muted-foreground">Insectos capturados: </span>
                                    <b>{last.insect_count}</b>
                                    {last.uv_insect_type && (
                                        <span className="text-muted-foreground"> ({last.uv_insect_type})</span>
                                    )}
                                </div>
                            )}
                            {last.notes && (
                                <div className="text-[11px] bg-slate-50 rounded p-1.5 border border-slate-100">
                                    <span className="text-muted-foreground">Observación: </span>
                                    {last.notes}
                                </div>
                            )}
                            <div className="text-[10px] text-muted-foreground flex items-center gap-1 pt-0.5">
                                <CalendarIcon className="w-3 h-3" /> Inspeccionado el {fmtDate(last.inspected_at)}
                            </div>
                        </div>
                    ) : (
                        <div className="border-t pt-2.5 text-[11px] text-muted-foreground italic">
                            Sin inspecciones registradas en los últimos {months} meses.
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

/* ----------------------------- Page ----------------------------- */
export default function PortalMaps() {
    const [plans, setPlans] = useState([]);
    const [planId, setPlanId] = useState("");
    const [devices, setDevices] = useState([]);
    const [heatmap, setHeatmap] = useState(null);
    const [deviceSummary, setDeviceSummary] = useState({}); // { device_id: {last_inspection, events_count} }
    const [showHeatmap, setShowHeatmap] = useState(true);
    const [showMarkers, setShowMarkers] = useState(true);
    const [months, setMonths] = useState(12);
    const [zoom, setZoom] = useState(1);
    const { branchParam } = useBranch();

    // load plans
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/floor-plans", { params: branchParam });
                setPlans(data);
                if (data.length && !planId) setPlanId(data[0].id);
                else if (!data.length) setPlanId(null);
            } catch (e) {
                console.error("floor-plans load", e);
            }
        })();
    }, [branchParam]);

    const plan = useMemo(() => plans.find((p) => p.id === planId), [plans, planId]);

    // Reset zoom when switching plans
    useEffect(() => { setZoom(1); }, [planId]);

    // devices, heatmap, per-device summary
    useEffect(() => {
        if (!planId) return;
        let alive = true;
        (async () => {
            try {
                const [d, h, s] = await Promise.all([
                    api.get("/pest-devices", { params: { floor_plan_id: planId } }),
                    api.get("/portal/insights/heatmap", { params: { floor_plan_id: planId, months } }),
                    api.get("/portal/insights/device-summary", { params: { floor_plan_id: planId, months } }),
                ]);
                if (!alive) return;
                setDevices(d.data || []);
                setHeatmap(h.data || null);
                setDeviceSummary(s.data?.summary || {});
            } catch (e) {
                console.error("plan data load", e);
            }
        })();
        return () => { alive = false; };
    }, [planId, months]);

    // Derived legend: counts by zone × type × subtype
    const legendBreakdown = useMemo(() => buildLegend(devices), [devices]);

    return (
        <div data-testid="portal-maps">
            <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold mb-1" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                        Mapas de instalación
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Ubicación de dispositivos y mapa de calor histórico de actividad.
                        Toca cualquier dispositivo para ver su detalle e historial.
                    </p>
                </div>
                <Link to="/portal/insights">
                    <Button variant="outline" data-testid="go-to-insights-button">
                        <BarChart3 className="w-4 h-4 mr-1.5" /> Ver estadísticas
                    </Button>
                </Link>
            </div>

            {plans.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                        Aún no hay planos disponibles para tu cliente.
                    </CardContent>
                </Card>
            ) : (
                <div className="grid lg:grid-cols-[300px_1fr] gap-4">
                    {/* Sidebar */}
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-1.5">
                                    <MapIcon className="w-4 h-4" /> Planos disponibles
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-1.5">
                                {plans.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setPlanId(p.id)}
                                        className={`w-full text-left px-2.5 py-2 rounded-lg text-sm border transition ${
                                            p.id === planId
                                                ? "bg-[hsl(var(--trulyn-navy))] text-white border-transparent"
                                                : "hover:bg-slate-50 border-slate-200"
                                        }`}
                                        data-testid={`plan-item-${p.id}`}
                                    >
                                        <div className="font-medium truncate">{p.name}</div>
                                    </button>
                                ))}

                                <div className="border-t mt-3 pt-3 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="show-markers" className="text-xs cursor-pointer">Marcadores</Label>
                                        <Switch id="show-markers" checked={showMarkers} onCheckedChange={setShowMarkers}
                                                data-testid="toggle-markers" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="show-heatmap" className="text-xs cursor-pointer flex items-center gap-1">
                                            <Flame className="w-3 h-3 text-orange-500" /> Mapa de calor
                                        </Label>
                                        <Switch id="show-heatmap" checked={showHeatmap} onCheckedChange={setShowHeatmap}
                                                data-testid="toggle-heatmap" />
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Periodo</Label>
                                        <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v, 10))}>
                                            <SelectTrigger className="h-8 text-xs mt-1" data-testid="maps-months-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="3">3 meses</SelectItem>
                                                <SelectItem value="6">6 meses</SelectItem>
                                                <SelectItem value="12">12 meses</SelectItem>
                                                <SelectItem value="24">24 meses</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {heatmap && (
                                    <div className="border-t mt-3 pt-3 text-[11px] text-muted-foreground space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span>Eventos totales</span>
                                            <Badge variant="secondary" data-testid="heatmap-events-count">{heatmap.total_events}</Badge>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span>Puntos calientes</span>
                                            <Badge variant="secondary">{heatmap.points.length}</Badge>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Detailed legend */}
                        <Card data-testid="map-legend-card">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-1.5">
                                    <Layers className="w-4 h-4" /> Inventario por zona
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {legendBreakdown.length === 0 ? (
                                    <div className="text-xs text-muted-foreground">Este plano aún no tiene dispositivos.</div>
                                ) : (
                                    legendBreakdown.map((zg) => (
                                        <div key={zg.zone} data-testid={`legend-zone-${zg.zone}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: ZONE_COLOR[zg.zone] }} />
                                                    <span className="text-xs font-semibold">{ZONE_LBL[zg.zone]}</span>
                                                </div>
                                                <Badge variant="secondary" className="text-[10px]">{zg.total}</Badge>
                                            </div>
                                            <ul className="ml-4 space-y-0.5">
                                                {zg.lines.map((ln, idx) => (
                                                    <li key={idx} className="text-[11px] text-slate-600 flex items-center justify-between">
                                                        <span className="flex items-center gap-1.5">
                                                            <span className="w-1.5 h-1.5 rounded-full"
                                                                  style={{ background: ln.is_uv ? UV_COLOR : ZONE_COLOR[zg.zone] }} />
                                                            {ln.label}
                                                        </span>
                                                        <span className="font-mono font-semibold">{ln.count}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Canvas */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2 justify-between">
                                <span>{plan?.name || "Plano"}</span>
                                {showHeatmap && (heatmap?.total_events || 0) > 0 && (
                                    <span className="text-xs font-normal text-muted-foreground inline-flex items-center gap-1">
                                        <Flame className="w-3.5 h-3.5 text-orange-500" />
                                        Heatmap — últimos {months} meses
                                    </span>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!plan ? (
                                <div className="h-[400px] flex items-center justify-center text-muted-foreground text-sm">
                                    Selecciona un plano.
                                </div>
                            ) : (
                                <div className="relative" data-testid="map-canvas-wrap">
                                    {/* Zoom controls */}
                                    <div className="absolute top-3 right-3 z-20 flex flex-col gap-1">
                                        <Button variant="secondary" size="icon" className="h-8 w-8 shadow border bg-white/95 hover:bg-white"
                                                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                                                disabled={zoom >= ZOOM_MAX} data-testid="map-zoom-in" aria-label="Acercar">
                                            <ZoomIn className="w-4 h-4" />
                                        </Button>
                                        <Button variant="secondary" size="icon" className="h-8 w-8 shadow border bg-white/95 hover:bg-white"
                                                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                                                disabled={zoom <= ZOOM_MIN} data-testid="map-zoom-out" aria-label="Alejar">
                                            <ZoomOut className="w-4 h-4" />
                                        </Button>
                                        <Button variant="secondary" size="icon" className="h-8 w-8 shadow border bg-white/95 hover:bg-white"
                                                onClick={() => setZoom(1)} disabled={zoom === 1} data-testid="map-zoom-reset" aria-label="Restablecer zoom">
                                            <RotateCcw className="w-4 h-4" />
                                        </Button>
                                        <div className="text-[10px] font-semibold text-center text-slate-600 bg-white/95 border rounded px-1 py-0.5 shadow" data-testid="map-zoom-level">
                                            {Math.round(zoom * 100)}%
                                        </div>
                                    </div>

                                    <div className="w-full overflow-auto rounded-lg border bg-slate-100" style={{ maxHeight: "72vh" }} data-testid="map-viewport">
                                        <div className="relative origin-top-left" style={{ width: `${zoom * 100}%` }} data-testid="map-canvas">
                                            <img
                                                src={assetUrl(plan.image_url)}
                                                alt={plan.name}
                                                className="w-full h-auto block select-none"
                                                draggable={false}
                                            />

                                            {/* Named areas */}
                                            {(plan.areas || []).map((a) => (
                                                <div
                                                    key={a.area_id}
                                                    className="absolute border-2 border-dashed border-slate-600/60 bg-slate-900/5 rounded"
                                                    style={{
                                                        left: `${a.x}%`,
                                                        top: `${a.y}%`,
                                                        width: `${a.w}%`,
                                                        height: `${a.h}%`,
                                                    }}
                                                    title={a.name}
                                                >
                                                    <div className="absolute -top-5 left-0 bg-slate-800 text-white text-[10px] px-1.5 py-0.5 rounded">
                                                        {a.name}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Heatmap */}
                                            {showHeatmap && heatmap?.points?.map((p) => {
                                                const size = 6 + 12 * p.intensity;
                                                const alpha = 0.25 + 0.55 * p.intensity;
                                                return (
                                                    <div
                                                        key={`hm-${p.device_id}`}
                                                        className="absolute pointer-events-none"
                                                        style={{
                                                            left: `${p.x}%`,
                                                            top: `${p.y}%`,
                                                            width: `${size}%`,
                                                            aspectRatio: "1/1",
                                                            transform: "translate(-50%, -50%)",
                                                            background: `radial-gradient(circle, rgba(239,68,68,${alpha}) 0%, rgba(249,115,22,${alpha * 0.7}) 35%, rgba(249,115,22,0) 70%)`,
                                                            filter: "blur(2px)",
                                                            mixBlendMode: "multiply",
                                                        }}
                                                        data-testid={`heatpoint-${p.device_id}`}
                                                    />
                                                );
                                            })}

                                            {/* Markers with Popover */}
                                            {showMarkers && devices.map((d) => (
                                                <DeviceMarker
                                                    key={d.id}
                                                    device={d}
                                                    summary={deviceSummary[d.id]}
                                                    months={months}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5">
                                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                                <span>
                                    Toca un dispositivo para ver detalle y el historial.
                                    El mapa de calor combina capturas, consumo de cebo e insectos UV para resaltar zonas con más actividad histórica.
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

/* ----------------------------- Legend builder ----------------------------- */
function buildLegend(devices) {
    // Groups devices by zone → device_type (+ subtype) and returns ordered list
    const ORDER_ZONE = ["verde", "amarilla", "roja"];
    const out = {};
    for (const d of devices) {
        const z = d.zone || "—";
        if (!out[z]) out[z] = { zone: z, total: 0, byKey: {} };
        out[z].total += 1;

        let label;
        let key;
        const isUv = d.device_type === "lampara_uv";
        if (d.device_type === "trampa") {
            const sub = TRAP_SUBTYPE_LBL[d.trap_subtype] || "Trampa";
            label = sub;
            key = `trampa:${d.trap_subtype || "_"}`;
        } else if (d.device_type === "cebadero_cebo") {
            const sub = d.trap_subtype && TRAP_SUBTYPE_LBL[d.trap_subtype];
            label = sub ? `Cebadero (${sub.replace(/^Cebadero de /, "")})` : "Cebadero";
            key = `cebadero:${d.trap_subtype || "_"}`;
        } else if (isUv) {
            label = "Lámpara UV";
            key = "uv";
        } else {
            label = d.device_type || "Otro";
            key = `o:${d.device_type}`;
        }
        if (!out[z].byKey[key]) out[z].byKey[key] = { label, count: 0, is_uv: isUv };
        out[z].byKey[key].count += 1;
    }
    return ORDER_ZONE.filter((z) => out[z]).map((z) => ({
        zone: z,
        total: out[z].total,
        lines: Object.values(out[z].byKey).sort((a, b) => b.count - a.count),
    }));
}
